import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FiPlus, FiEdit2, FiTrash2, FiExternalLink, FiX, FiCheck, FiAlertCircle, FiImage, FiSearch } from "react-icons/fi";
import { apiRequest, errorMessage } from "../../api";
import { CATEGORY_THEME } from "../../components/blog/BlogCard";
import "../../components/blog/blog.css";
import countWords from "./countWords";

const MIN_WORDS = 1000;
const inputClass = "w-full px-3 py-2 rounded-md bg-white/5 border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light";
const labelClass = "block text-xs text-gray-400 mb-1";
const CATEGORY_KEYS = Object.keys(CATEGORY_THEME);
const QUILL_MODULES = {
  toolbar: [[{ header: [2, 3, false] }], ["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["blockquote", "link"], ["clean"]],
};

const emptyForm = () => ({
  title: "", slug: "", category: CATEGORY_KEYS[0], excerpt: "", content: "", thumbnail_alt: "", meta_title: "", meta_description: "",
  keywords: "", author_name: "Mr. Aali Online Technologies Team", published_at: toLocalInput(new Date().toISOString()), is_published: true, is_featured: false,
});

function toLocalInput(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function Check({ ok, children }) {
  return (
    <li className={`flex items-start gap-2 text-xs ${ok ? "text-emerald-400" : "text-amber-300"}`}>
      {ok ? <FiCheck className="mt-0.5 shrink-0" /> : <FiAlertCircle className="mt-0.5 shrink-0" />}<span>{children}</span>
    </li>
  );
}

// How the post will look in a Google result, and what to improve so that it ranks
function SeoPanel({ form, words, hasThumbnail }) {
  const title = (form.meta_title || form.title || "Your title").slice(0, 70);
  const description = (form.meta_description || form.excerpt || "Your description").slice(0, 170);
  const slug = form.slug || "your-post-address";
  const titleLength = (form.meta_title || form.title).length;
  const descLength = (form.meta_description || form.excerpt).length;
  const keywords = form.keywords.split(",").map((k) => k.trim()).filter(Boolean);
  const main = (keywords[0] || "").toLowerCase();
  return (
    <div className="rounded-xl border border-navy-border bg-white/[0.03] p-4">
      <p className="text-sm font-semibold mb-3">Search engine preview</p>
      <div className="rounded-lg bg-white p-4 mb-4">
        <p className="text-xs text-gray-600 truncate">mraalionline.com &rsaquo; blog &rsaquo; {slug}</p>
        <p className="text-lg leading-snug text-[#1a0dab] mt-0.5 line-clamp-2">{title}</p>
        <p className="text-sm text-gray-700 mt-1 line-clamp-2">{description}</p>
      </div>
      <ul className="grid md:grid-cols-2 gap-x-6 gap-y-1.5">
        <Check ok={words >= MIN_WORDS}>{words >= MIN_WORDS ? `${words} words - long, in-depth articles rank best` : `${words} of ${MIN_WORDS} words - write ${MIN_WORDS - words} more to publish`}</Check>
        <Check ok={titleLength >= 30 && titleLength <= 60}>Search title is {titleLength} characters (30-60 is ideal)</Check>
        <Check ok={descLength >= 120 && descLength <= 160}>Search description is {descLength} characters (120-160 is ideal)</Check>
        <Check ok={keywords.length >= 3}>{keywords.length} keyword(s) - add at least 3, the first is the main one</Check>
        <Check ok={!main || (form.title.toLowerCase().includes(main))}>{main ? "The main keyword is in the title" : "Set a main keyword (first keyword)"}</Check>
        <Check ok={!main || (form.excerpt + " " + form.meta_description).toLowerCase().includes(main)}>The main keyword is in the summary or description</Check>
        <Check ok={hasThumbnail}>A thumbnail is set (used when the link is shared)</Check>
        <Check ok={!hasThumbnail || form.thumbnail_alt.trim().length > 0}>The thumbnail has a description (alt text)</Check>
        <Check ok={/<h2[ >]/i.test(form.content)}>The article has section headings (H2)</Check>
        <Check ok={/<h2[^>]*>\s*frequently asked questions/i.test(form.content)}>An FAQ section ("Frequently Asked Questions") earns rich results</Check>
      </ul>
    </div>
  );
}

export default function BlogPostsAdmin() {
  const formRef = useRef(null);
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(null);       // the saved post being edited, null when writing a new one
  const [form, setForm] = useState(emptyForm());
  const [thumb, setThumb] = useState(null);
  const [removeThumb, setRemoveThumb] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [filter, setFilter] = useState({ category: "", status: "", search: "" });

  const load = useCallback(async () => {
    const { ok, data } = await apiRequest("/core/blog-posts/", { auth: true });
    if (ok) setPosts(data);
    else setError("The posts could not be loaded.");
    setLoaded(true);
  }, []);
  useEffect(() => { load(); }, [load]);

  const words = useMemo(() => countWords(form.content), [form.content]);
  const thumbPreview = useMemo(() => (thumb ? URL.createObjectURL(thumb) : removeThumb ? "" : editing?.thumbnail || ""), [thumb, removeThumb, editing]);
  useEffect(() => () => { if (thumb && thumbPreview) URL.revokeObjectURL(thumbPreview); }, [thumb, thumbPreview]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const reset = () => { setEditing(null); setForm(emptyForm()); setThumb(null); setRemoveThumb(false); setFormKey((k) => k + 1); };

  const startEdit = async (post) => {
    setError(""); setNotice("");
    const { ok, data } = await apiRequest(`/core/blog-posts/${post.id}/`, { auth: true });
    if (!ok) return setError("This post could not be opened.");
    setEditing(data);
    setForm({
      title: data.title, slug: data.slug, category: data.category, excerpt: data.excerpt, content: data.content, thumbnail_alt: data.thumbnail_alt,
      meta_title: data.meta_title, meta_description: data.meta_description, keywords: data.keywords, author_name: data.author_name,
      published_at: toLocalInput(data.published_at), is_published: data.is_published, is_featured: data.is_featured,
    });
    setThumb(null); setRemoveThumb(false); setFormKey((k) => k + 1);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice("");
    if (form.is_published && words < MIN_WORDS) {
      return setError(`A published post needs at least ${MIN_WORDS} words - this one has ${words}. Write more, or untick "Published" to save it as a draft.`);
    }
    setBusy(true);
    const fd = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key === "published_at") { if (value) fd.append(key, new Date(value).toISOString()); }
      else if (key === "slug") { if (value) fd.append(key, value); }
      else fd.append(key, typeof value === "boolean" ? (value ? "true" : "false") : value);
    });
    if (thumb) fd.append("thumbnail", thumb);
    else if (removeThumb) fd.append("thumbnail", "");
    const res = await apiRequest(editing ? `/core/blog-posts/${editing.id}/` : "/core/blog-posts/", { method: editing ? "PATCH" : "POST", auth: true, body: fd });
    setBusy(false);
    if (!res.ok) return setError(errorMessage(res.data));
    setNotice(`"${res.data.title}" was ${editing ? "updated" : "created"}${res.data.is_published ? " and is live." : " as a draft."}`);
    await load();
    reset();
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    const res = await apiRequest(`/core/blog-posts/${post.id}/`, { method: "DELETE", auth: true });
    if (!res.ok) return setError("The post could not be deleted.");
    if (editing?.id === post.id) reset();
    load();
  };

  const shown = posts.filter((p) =>
    (!filter.category || p.category === filter.category)
    && (!filter.status || (filter.status === "draft" ? !p.is_published : p.is_published))
    && (!filter.search || p.title.toLowerCase().includes(filter.search.toLowerCase())));
  const published = posts.filter((p) => p.is_published).length;
  const totalViews = posts.reduce((sum, p) => sum + (p.views || 0), 0);

  return (
    <div className="w-full">
      <h1 className="text-2xl font-bold mb-2">Blog Posts</h1>
      <p className="text-sm text-gray-400 mb-6">
        Write the articles shown on the Blogs page and its four category pages. A published article needs at least {MIN_WORDS} words; the search-engine preview below the
        editor shows how it will look on Google and what to improve. Upload each article's thumbnail here - until then a branded picture of its category is shown.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[["Posts", posts.length], ["Published", published], ["Drafts", posts.length - published], ["Total views", totalViews.toLocaleString()]].map(([label, value]) => (
          <div key={label} className="rounded-xl border border-navy-border bg-navy px-4 py-3"><p className="text-xs text-gray-400">{label}</p><p className="text-2xl font-bold">{value}</p></div>
        ))}
      </div>

      {error && <p className="text-red-400 mb-4" role="alert">{error}</p>}
      {notice && <p className="text-emerald-400 mb-4" role="status">{notice}</p>}

      <form ref={formRef} key={formKey} onSubmit={submit} className="mb-8 bg-navy border border-navy-border rounded-xl p-6 scroll-mt-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{editing ? `Edit "${editing.title}"` : "Write a New Post"}</h2>
          {editing && <button type="button" onClick={reset} className="flex items-center gap-1 text-xs text-gray-400 hover:text-white"><FiX size={14} /> Cancel edit</button>}
        </div>

        <div className="grid md:grid-cols-[1fr_260px] gap-4">
          <div>
            <label className={labelClass} htmlFor="blog-title">Title *</label>
            <input id="blog-title" required maxLength={150} className={inputClass} value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="A headline people want to click" />
          </div>
          <div>
            <label className={labelClass} htmlFor="blog-category">Category *</label>
            <select id="blog-category" className={inputClass} value={form.category} onChange={(e) => set({ category: e.target.value })}>
              {CATEGORY_KEYS.map((key) => <option key={key} value={key} className="text-black">{CATEGORY_THEME[key].label}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className={labelClass} htmlFor="blog-excerpt">Summary * <span className="text-gray-500">({form.excerpt.length}/320) - shown on the cards and as the first words of the article's page</span></label>
          <textarea id="blog-excerpt" required rows={2} maxLength={320} className={inputClass} value={form.excerpt} onChange={(e) => set({ excerpt: e.target.value })} />
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-1">
            <label className={labelClass.replace("mb-1", "")}>Article *</label>
            <span className={`text-xs font-semibold ${words >= MIN_WORDS ? "text-emerald-400" : "text-amber-300"}`}>{words} / {MIN_WORDS} words</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-2"><div className={`h-full ${words >= MIN_WORDS ? "bg-emerald-400" : "bg-amber-400"}`} style={{ width: `${Math.min(100, (words / MIN_WORDS) * 100)}%` }} /></div>
          <div className="bg-white text-black rounded-md blog-editor">
            <ReactQuill theme="snow" modules={QUILL_MODULES} value={form.content} onChange={(value) => set({ content: value })} />
          </div>
          <p className="text-xs text-gray-500 mt-1">Use Heading 2 for the sections (they make the table of contents) and Heading 3 for sub-sections. Add a section named "Frequently Asked Questions" with each question as Heading 3 and the answer below it for Google's FAQ results.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-5">
          <div>
            <p className="text-sm text-gray-300 mb-2 flex items-center gap-2"><FiImage /> Thumbnail <span className="text-xs text-gray-500">(1200 x 630 px works best)</span></p>
            {thumbPreview ? <img src={thumbPreview} alt="Thumbnail" className="w-full max-w-xs aspect-[16/9] object-cover rounded-md border border-navy-border mb-2" />
              : <div className="w-full max-w-xs aspect-[16/9] rounded-md border border-dashed border-navy-border flex items-center justify-center text-xs text-gray-500 mb-2">No thumbnail yet - the category picture is shown</div>}
            <input type="file" accept="image/*" onChange={(e) => { setThumb(e.target.files[0] || null); setRemoveThumb(false); }} className="text-xs text-gray-400" aria-label="Choose thumbnail" />
            {editing?.thumbnail && !thumb && (
              <label className="flex items-center gap-2 text-xs text-gray-400 mt-2"><input type="checkbox" checked={removeThumb} onChange={(e) => setRemoveThumb(e.target.checked)} /> Remove the current thumbnail</label>
            )}
            <label className={`${labelClass} mt-3`} htmlFor="blog-alt">Thumbnail description (alt text)</label>
            <input id="blog-alt" maxLength={160} className={inputClass} value={form.thumbnail_alt} onChange={(e) => set({ thumbnail_alt: e.target.value })} placeholder="What the picture shows" />
          </div>
          <div className="space-y-4">
            <div>
              <label className={labelClass} htmlFor="blog-keywords">Keywords <span className="text-gray-500">(comma-separated; the first is the main one)</span></label>
              <input id="blog-keywords" maxLength={300} className={inputClass} value={form.keywords} onChange={(e) => set({ keywords: e.target.value })} placeholder="custom software development, erp, web app" />
            </div>
            <div>
              <label className={labelClass} htmlFor="blog-meta-title">Search title <span className="text-gray-500">({form.meta_title.length}/70, leave empty to use the title)</span></label>
              <input id="blog-meta-title" maxLength={70} className={inputClass} value={form.meta_title} onChange={(e) => set({ meta_title: e.target.value })} />
            </div>
            <div>
              <label className={labelClass} htmlFor="blog-meta-description">Search description <span className="text-gray-500">({form.meta_description.length}/170, leave empty to use the summary)</span></label>
              <textarea id="blog-meta-description" rows={2} maxLength={170} className={inputClass} value={form.meta_description} onChange={(e) => set({ meta_description: e.target.value })} />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <div>
            <label className={labelClass} htmlFor="blog-slug">Web address <span className="text-gray-500">(empty = made from the title)</span></label>
            <input id="blog-slug" maxLength={140} pattern="[a-zA-Z0-9_\-]*" className={inputClass} value={form.slug} onChange={(e) => set({ slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })} placeholder="my-article-address" />
          </div>
          <div>
            <label className={labelClass} htmlFor="blog-author">Author</label>
            <input id="blog-author" maxLength={100} className={inputClass} value={form.author_name} onChange={(e) => set({ author_name: e.target.value })} />
          </div>
          <div>
            <label className={labelClass} htmlFor="blog-date">Publish date</label>
            <input id="blog-date" type="datetime-local" className={inputClass} value={form.published_at} onChange={(e) => set({ published_at: e.target.value })} />
          </div>
        </div>

        <div className="mt-5"><SeoPanel form={form} words={words} hasThumbnail={!!thumbPreview} /></div>

        <div className="flex flex-wrap items-center gap-6 mt-5">
          <label className="flex items-center gap-2 text-sm text-gray-300"><input type="checkbox" checked={form.is_published} onChange={(e) => set({ is_published: e.target.checked })} /> Published (visible on the website)</label>
          <label className="flex items-center gap-2 text-sm text-gray-300"><input type="checkbox" checked={form.is_featured} onChange={(e) => set({ is_featured: e.target.checked })} /> Featured (shown first)</label>
          <button disabled={busy} className="ml-auto px-5 py-2 rounded-md bg-brand-gradient text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
            {busy ? "Saving..." : editing ? "Save Changes" : <><FiPlus /> Add Post</>}
          </button>
        </div>
      </form>

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h2 className="text-lg font-semibold mr-auto">All posts ({shown.length})</h2>
        <label className="relative"><span className="sr-only">Search posts</span><FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={14} /><input className={`${inputClass} pl-9 w-52`} placeholder="Search posts..." value={filter.search} onChange={(e) => setFilter({ ...filter, search: e.target.value })} /></label>
        <select aria-label="Filter by category" className={`${inputClass} w-auto`} value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })}>
          <option value="" className="text-black">All categories</option>
          {CATEGORY_KEYS.map((key) => <option key={key} value={key} className="text-black">{CATEGORY_THEME[key].label}</option>)}
        </select>
        <select aria-label="Filter by status" className={`${inputClass} w-auto`} value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
          <option value="" className="text-black">Any status</option><option value="published" className="text-black">Published</option><option value="draft" className="text-black">Drafts</option>
        </select>
      </div>

      <div className="bg-navy border border-navy-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-gray-400 text-left">
            <tr><th className="px-4 py-3 w-24">Thumbnail</th><th className="px-4 py-3">Post</th><th className="px-4 py-3">Category</th><th className="px-4 py-3 w-20">Words</th><th className="px-4 py-3 w-20">Views</th><th className="px-4 py-3 w-24">Status</th><th className="px-4 py-3 w-36"></th></tr>
          </thead>
          <tbody>
            {shown.map((post) => (
              <tr key={post.id} className="border-t border-navy-border align-middle">
                <td className="px-4 py-3">
                  {post.thumbnail ? <img src={post.thumbnail} alt="" className="w-16 h-10 object-cover rounded-md border border-navy-border" />
                    : <div className="w-16 h-10 rounded-md border border-dashed border-navy-border flex items-center justify-center text-gray-600"><FiImage size={14} /></div>}
                </td>
                <td className="px-4 py-3 max-w-md"><p className="text-white font-medium">{post.title}</p><p className="text-xs text-gray-500">/blog/{post.slug} {post.is_featured && <span className="text-amber-300">&middot; Featured</span>}</p></td>
                <td className="px-4 py-3 text-gray-400">{post.category_label}</td>
                <td className="px-4 py-3 text-gray-400">{post.word_count}</td>
                <td className="px-4 py-3 text-gray-400">{post.views}</td>
                <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs ${post.is_published ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-300"}`}>{post.is_published ? "Published" : "Draft"}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => startEdit(post)} className="p-1.5 rounded-md text-accent-light hover:bg-accent-light/10 flex items-center gap-1 text-xs"><FiEdit2 size={14} /> Edit</button>
                    <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-md text-gray-400 hover:bg-white/10" aria-label={`View ${post.title}`}><FiExternalLink size={14} /></a>
                    <button onClick={() => remove(post)} className="p-1.5 rounded-md text-red-400 hover:bg-red-500/10" aria-label={`Delete ${post.title}`}><FiTrash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {loaded && shown.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">{posts.length ? "No post matches these filters." : "No posts yet - write one above."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
